import { ApiClient } from './ApiClient';
import { OpenAPI } from './core/OpenAPI';
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { jwtDecode } from 'jwt-decode';
import apiClient from './client';

// Configure OpenAPI base once for the entire app.
// Use explicit backend base URL by default so the browser can reach it directly.
// You can override via REACT_APP_API_URL in your .env.
(() => {
  const fallback = 'http://localhost:8000';
  const envBase = (process.env.REACT_APP_API_URL || '').trim();
  let base = envBase || fallback;
  try {
    // Validate URL; if invalid, fall back
    // eslint-disable-next-line no-new
    new URL(base);
  } catch {
    base = fallback;
  }
  OpenAPI.BASE = base;
})();

// Get public user email from environment (configured to match backend PUBLIC_USER_EMAIL)
const PUBLIC_USER_EMAIL = process.env.REACT_APP_PUBLIC_USER_EMAIL || null;

// Helper to get user email from token
const getUserEmailFromToken = (token: string | null): string | null => {
  if (!token) return null;
  try {
    const decoded = jwtDecode<{ email?: string }>(token);
    return decoded.email || null;
  } catch {
    return null;
  }
};

// Helper to check if current user is the public user
const isPublicUser = (token: string | null): boolean => {
  if (!PUBLIC_USER_EMAIL || !token) return false;
  const userEmail = getUserEmailFromToken(token);
  return userEmail === PUBLIC_USER_EMAIL;
};

// Helper to check if token is expired
const isTokenExpired = (token: string | null): boolean => {
  if (!token) return true;
  try {
    const decoded = jwtDecode<{ exp: number }>(token);
    const now = Date.now();
    const expiry = decoded.exp * 1000;
    return now >= expiry;
  } catch {
    return true;
  }
};

// Helper to clear all tokens (only if not public user)
const clearAllTokens = (token?: string | null) => {
  // Don't clear tokens for public user - they should remain logged in
  if (token && isPublicUser(token)) {
    const SHOULD_DEBUG = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_ENABLE_DEBUG_LOGGING === 'true';
    if (SHOULD_DEBUG) console.log('OpenAPI Client - Public user detected, preserving session');
    return;
  }
  
  localStorage.removeItem('auth_access_token');
  localStorage.removeItem('auth_refresh_token');
  localStorage.removeItem('auth_token_expiry');
  OpenAPI.TOKEN = undefined;
  apiClient.clearTokens();
  updateApiToken(null);
  
  // Dispatch custom event to trigger logout in AuthProvider
  window.dispatchEvent(new CustomEvent('auth:token-expired'));
};

// List of endpoints that allow unauthenticated access (public endpoints)
const PUBLIC_ENDPOINTS = [
  '/api/v1/public/',           // Public access info
  '/api/v1/graph/roots',       // Graph roots (shows public nodes for unauthenticated users)
  '/api/v1/graph/incoming/',   // Graph incoming (allows Optional[User])
  '/api/v1/graph/incoming-uneven/',  // Graph incoming uneven
  '/api/v1/graph/incoming-even/',    // Graph incoming even
  '/api/v1/graph/incoming-grouped-hs/', // Graph incoming grouped
  '/api/v1/graph/incoming-grouped-prodcom/', // Graph incoming grouped (PRODCOM)
  '/api/v1/prodcom/',        // PRODCOM metadata (countries, years, indicators)
  '/api/v1/auth/register',     // Registration
  '/api/v1/auth/login',        // Login
  '/api/v1/auth/generate-totp', // TOTP generation
  '/api/v1/auth/verify-totp',  // TOTP verification
  '/api/v1/health',            // Health check
  '/api/v1/trade/',            // Trade endpoints (public data - years, country-world, rca, etc.)
  '/api/v1/countries',         // Countries list (public data)
];

// Check if an endpoint allows unauthenticated access
const isPublicEndpoint = (url: string | undefined): boolean => {
  if (!url) return false;
  return PUBLIC_ENDPOINTS.some(endpoint => url.includes(endpoint));
};

// Add Axios request interceptor to check token expiration before requests
axios.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const SHOULD_DEBUG = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_ENABLE_DEBUG_LOGGING === 'true';
    const token = localStorage.getItem('auth_access_token');
    
    // Only check for API requests, not external URLs
    if (config.url && (config.url.startsWith('/api/') || config.url.includes(OpenAPI.BASE))) {
      // Allow public endpoints to proceed without token
      if (isPublicEndpoint(config.url)) {
        if (SHOULD_DEBUG && !token) {
          console.log('OpenAPI Client - Public endpoint, allowing request without token:', config.url);
        }
        // If token exists, still include it (for public user)
        if (token && config.headers && !config.headers['Authorization']) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config; // Allow public endpoints to proceed
      }
      
      // For non-public endpoints, require token
      if (!token) {
        if (SHOULD_DEBUG) console.log('OpenAPI Client - No token available for protected endpoint, rejecting request');
        clearAllTokens();
        return Promise.reject(new Error('Authentication failed - please log in again'));
      }
      
      // Check if current user is public user
      const isPublic = isPublicUser(token);
      
      // Check if token is expired
      if (isTokenExpired(token)) {
        if (SHOULD_DEBUG) console.log('OpenAPI Client - Token expired, attempting refresh', { isPublicUser: isPublic });
        
        // For public user, be more lenient - try refresh but don't aggressively logout
        try {
          await apiClient.refreshAccessToken();
          // Update token in config after refresh
          const newToken = apiClient.getAccessToken();
          if (newToken && config.headers) {
            config.headers['Authorization'] = `Bearer ${newToken}`;
            if (SHOULD_DEBUG) console.log('OpenAPI Client - Token refreshed successfully');
          } else {
            if (SHOULD_DEBUG) console.log('OpenAPI Client - Refresh failed, no new token');
            // For public user, don't clear tokens - let the request proceed and handle 401 gracefully
            if (!isPublic) {
              clearAllTokens(token);
              return Promise.reject(new Error('Authentication failed - please log in again'));
            }
            // For public user, continue with expired token and let backend handle it
            if (SHOULD_DEBUG) console.log('OpenAPI Client - Public user, allowing request with expired token');
          }
        } catch (error) {
          if (SHOULD_DEBUG) console.error('OpenAPI Client - Token refresh failed:', error);
          // For public user, don't clear tokens - let the request proceed
          if (!isPublic) {
            clearAllTokens(token);
            return Promise.reject(new Error('Authentication failed - please log in again'));
          }
          // For public user, continue with expired token and let backend handle it
          if (SHOULD_DEBUG) console.log('OpenAPI Client - Public user, allowing request despite refresh failure');
        }
      } else {
        // Token is valid, ensure it's set in headers
        if (config.headers && !config.headers['Authorization']) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add Axios response interceptor to handle 401 errors
axios.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const SHOULD_DEBUG = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_ENABLE_DEBUG_LOGGING === 'true';
    
    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
      const requestUrl = originalRequest?.url || '';
      const token = localStorage.getItem('auth_access_token');
      const isPublic = isPublicUser(token);
      
      // For public endpoints without tokens, 401 might mean "this resource requires auth"
      // Don't try to refresh tokens for unauthenticated requests to public endpoints
      if (isPublicEndpoint(requestUrl) && !token) {
        if (SHOULD_DEBUG) console.log('OpenAPI Client - 401 on public endpoint without token, allowing error to propagate');
        return Promise.reject(error); // Let the component handle it (might be trying to access protected data)
      }
      
      // Don't retry if we already tried
      if (originalRequest._retry) {
        if (SHOULD_DEBUG) console.log('OpenAPI Client - Already retried 401, checking if should logout', { isPublicUser: isPublic, url: requestUrl });
        // We already tried refreshing and retrying, but still got 401
        // This means either:
        // 1. Refresh failed (token invalid) - should logout
        // 2. Refresh succeeded but user doesn't have permission for this resource - still logout to be safe
        // For public user, preserve session (they might still access public paths)
        // For non-public user, logout
        if (!isPublic) {
          if (SHOULD_DEBUG) console.log('OpenAPI Client - Non-public user got 401 after refresh retry, logging out');
          clearAllTokens(token);
          return Promise.reject(new Error('Authentication failed - please log in again'));
        }
        // For public user, preserve session (might be trying to access a resource they don't have permission for)
        if (SHOULD_DEBUG) console.log('OpenAPI Client - Public user 401 after retry, preserving session');
        return Promise.reject(error);
      }
      
      // Always try to refresh token if we have one (both public and private users will have tokens)
      if (!token) {
        // No token means this was an unauthenticated request
        // For public endpoints, this might be normal (trying to access protected data)
        if (SHOULD_DEBUG) console.log('OpenAPI Client - 401 without token, likely trying to access protected resource');
        return Promise.reject(error);
      }
      
      originalRequest._retry = true;
      if (SHOULD_DEBUG) console.log('OpenAPI Client - Received 401, attempting token refresh', { isPublicUser: isPublic, url: requestUrl });
      
      try {
        await apiClient.refreshAccessToken();
        const newToken = apiClient.getAccessToken();
        if (newToken && originalRequest.headers) {
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          // Retry the original request
          if (SHOULD_DEBUG) console.log('OpenAPI Client - Token refreshed successfully, retrying request');
          return axios(originalRequest);
        } else {
          if (SHOULD_DEBUG) console.log('OpenAPI Client - Refresh failed, no new token', { isPublicUser: isPublic });
          // Refresh failed - decide whether to logout
          // For public user, preserve session (they might still access public paths)
          // For non-public user, logout
          if (!isPublic) {
            clearAllTokens(token);
            return Promise.reject(new Error('Authentication failed - please log in again'));
          }
          // For public user, preserve session
          if (SHOULD_DEBUG) console.log('OpenAPI Client - Public user, preserving session despite refresh failure');
          return Promise.reject(error);
        }
      } catch (refreshError) {
        if (SHOULD_DEBUG) console.error('OpenAPI Client - Token refresh failed on 401:', refreshError, { isPublicUser: isPublic });
        // Refresh failed - decide whether to logout
        // For public user, preserve session (they might still access public paths)
        // For non-public user, logout
        if (!isPublic) {
          clearAllTokens(token);
          return Promise.reject(new Error('Authentication failed - please log in again'));
        }
        // For public user, preserve session
        if (SHOULD_DEBUG) console.log('OpenAPI Client - Public user, preserving session despite refresh failure');
        return Promise.reject(error);
      }
    }
    
    return Promise.reject(error);
  }
);

// Create API client with token management
export const api = new ApiClient({ 
  BASE: OpenAPI.BASE,
  TOKEN: async () => {
    // Get token from localStorage
    const token = localStorage.getItem('auth_access_token');
    
    // Check if token is expired before returning it
    if (token && isTokenExpired(token)) {
      const SHOULD_DEBUG = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_ENABLE_DEBUG_LOGGING === 'true';
      const isPublic = isPublicUser(token);
      if (SHOULD_DEBUG) console.log('OpenAPI TOKEN resolver - Token expired, attempting refresh', { isPublicUser: isPublic });
      try {
        await apiClient.refreshAccessToken();
        const newToken = apiClient.getAccessToken();
        if (newToken) {
          return newToken;
        }
      } catch (error) {
        if (SHOULD_DEBUG) console.error('OpenAPI TOKEN resolver - Refresh failed:', error, { isPublicUser: isPublic });
        // For public user, return the token anyway (let backend handle expiration)
        if (isPublic) {
          if (SHOULD_DEBUG) console.log('OpenAPI TOKEN resolver - Public user, returning expired token');
          return token;
        }
        clearAllTokens(token);
        return '';
      }
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log('API Client - Retrieved token:', token ? token.substring(0, 20) + '...' : 'No token');
      // Do not log full tokens outside of scoped dev bursts
      // console.log('API Client - Full token:', token);
      console.log('API Client - localStorage keys:', Object.keys(localStorage));
      console.log('API Client - Token type:', typeof token);
      console.log('API Client - Token length:', token ? token.length : 0);
      console.log('API Client - Token is empty string:', token === '');
      console.log('API Client - Token is null:', token === null);
      console.log('API Client - Token is undefined:', token === undefined);
    }
    return token || '';
  }
});

// Function to update the API client token
export const updateApiToken = (token: string | null) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('updateApiToken called with:', token ? token.substring(0, 20) + '...' : 'null');
  }
  if (token) {
    OpenAPI.TOKEN = token;
    localStorage.setItem('auth_access_token', token);
    if (process.env.NODE_ENV === 'development') {
      console.log('updateApiToken - Token stored in localStorage and OpenAPI');
    }
  } else {
    OpenAPI.TOKEN = undefined;
    localStorage.removeItem('auth_access_token');
    if (process.env.NODE_ENV === 'development') {
      console.log('updateApiToken - Token removed from localStorage and OpenAPI');
    }
  }
};

export function toUserMessage(err: any) {
  const isProd = process.env.NODE_ENV === 'production';
  const data = err?.response?.data ?? {};
  const cid = data?.correlation_id;
  if (isProd) {
    return { message: 'Something went wrong. Please try again.', correlationId: cid };
  }
  const msg = data?.message || err?.message || 'Request failed';
  return { message: cid ? `${msg} (ref ${cid})` : msg, correlationId: cid };
}
